// dependency-graph-ignore-file

import { captureException } from '@sentry/core';

import { Event, EventConstructor, EventListener } from './types';

type MapKey = EventConstructor<Event<any>>;
type MapValue = Array<() => EventListener<any>>;

/**
 * Strongly-typed Event Bus that manages event listeners and dispatches events.
 * Each Event Bus only allows a predefined list of events.
 */
export class EventBus {
  #listeners = new Map<MapKey, MapValue>();

  /**
   * Registers a listener factory function for a specific event class.
   * A listener may not be re-registered twice.
   * TypeScript ensures type safety by matching the listener's expected event type.
   */
  register<T extends Event<any>>(
    eventClass: EventConstructor<T>,
    listenerFactory: () => EventListener<T>
  ): void {
    if (!this.#listeners.has(eventClass)) {
      this.#listeners.set(eventClass, []);
    }

    // remove the event and listener if they exist, to avoid duplicates
    this.remove(eventClass, listenerFactory);

    this.#listeners.get(eventClass)?.push(listenerFactory);
  }

  /**
   * Dispatches an event to all registered listeners.
   */
  dispatch<T extends Event<any>>(eventInstance: T): void {
    const eventType = eventInstance.constructor as EventConstructor<T>;
    const eventListeners = this.#listeners.get(eventType);

    if (!eventListeners || eventListeners.length === 0) {
      return;
    }

    eventListeners.forEach((factory) => {
      try {
        const listener = factory();

        listener.handle(eventInstance as any);
      } catch (e) {
        captureException(e);
      }
    });
  }

  /**
   * Removes a specific listener factory for an event.
   */
  remove<T extends Event<any>>(
    eventClass: EventConstructor<T>,
    listenerFactory: () => EventListener<T>
  ): void {
    const eventListeners = this.#listeners.get(eventClass);

    if (!eventListeners) {
      return;
    }

    const index = eventListeners.indexOf(listenerFactory);

    if (index !== -1) {
      eventListeners.splice(index, 1);
    }
  }

  /**
   * Clears all listeners for all events.
   */
  clear(): void {
    this.#listeners.clear();
  }
}
